import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

interface LoginFormData {
  email: string;
  password: string;
}

export const LoginForm: React.FC = () => {
  const [apiError, setApiError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isVerificationError, setIsVerificationError] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { register, handleSubmit, formState: { errors } } = useForm<LoginFormData>();

  // Get the redirect path from location state or default to home
  const from = (location.state as any)?.from || '/';

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setApiError(''); // Clear any previous errors
    setIsVerificationError(false); // Clear verification error state

    try {
      const result = await login(data.email, data.password);

      if (!result.success) {
        // Check if the error is related to email verification
        const errorMessage = result.error?.message || '';
        const errorDetail = result.error?.detail || '';
        const isEmailVerificationError =
          errorMessage.toLowerCase().includes('email verification') ||
          errorDetail.toLowerCase().includes('email verification') ||
          errorMessage.toLowerCase().includes('verify') ||
          errorDetail.toLowerCase().includes('verify') ||
          result.error?.code === 'account_email_verification_required' ||
          (result.error?.data?.flows?.some((flow: any) =>
            flow.id === 'verify_email' && flow.is_pending === true
          ));

        if (isEmailVerificationError) {
          setIsVerificationError(true);
          setApiError('Your email address has not been verified yet.');
        } else {
          setApiError(errorMessage || 'Login failed. Please check your credentials.');
        }
        return;
      }

      // Navigate to the attempted page or home, preserving query parameters
      navigate(from, { replace: true });
    } catch (error) {
      if (error instanceof Error) {
        // Check if the error message indicates email verification is required
        if (error.message.toLowerCase().includes('verification') ||
            error.message.toLowerCase().includes('verify')) {
          setIsVerificationError(true);
          setApiError('Your email address has not been verified yet.');
        } else {
          setApiError(error.message);
        }
      } else {
        setApiError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="scientia-card__content">
      <h1 className="scientia-card__title">Sign In</h1>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="field">
          <label className="label" htmlFor="email">Email</label>
          <div className="form-control">
            <input
              type="email"
              autoComplete="email"
              className={`input ${errors.email ? 'is-danger' : ''}`}
              id="email"
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              })}
            />
          </div>
          {errors.email && (
            <p className="help is-danger">{errors.email.message}</p>
          )}
        </div>

        <div className="field">
          <label className="label" htmlFor="password">Password</label>
          <div className="form-control">
            <input
              type="password"
              autoComplete="current-password"
              className={`input ${errors.password ? 'is-danger' : ''}`}
              id="password"
              {...register('password', {
                required: 'Password is required',
                minLength: {
                  value: 8,
                  message: 'Password must be at least 8 characters',
                },
              })}
            />
          </div>
          {errors.password && (
            <p className="help is-danger">{errors.password.message}</p>
          )}
        </div>

        {apiError && (
          <div className="form-message is-error" role="alert">
            {apiError}
            {isVerificationError && (
              <div className="verification-instructions mt-2">
                <p>Please check your email inbox for a verification link. You need to verify your email address before logging in.</p>
                <p className="mt-1">A verification email was sent when you created your account. If you don't see it, please check your spam folder.</p>
                <p className="mt-1">If you still can't find it, you can <a href="/auth/resend-verification" className="has-text-primary">request a new verification email</a>.</p>
              </div>
            )}
          </div>
        )}

        <div className="field">
          <div className="form-actions">
            <button
              type="submit"
              className={`button is-primary is-fullwidth ${isLoading ? 'is-loading' : ''}`}
              disabled={isLoading}
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </div>
        </div>

        <div className="has-text-centered mt-3">
          <a href="/auth/signup" className="has-text-primary">
            Don't have an account? Sign up
          </a>
        </div>
      </form>
    </div>
  );
};
